import React, { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../context/AuthContext';
import { AuthCard } from './AuthCard';
import BillingPanel from './BillingPanel';

// Shown in place of every page while the workspace is locked for billing:
// the trial ended or the subscription lapsed. Nothing has been deleted. An
// Admin can subscribe or fix the card from here; everyone else is told to
// ask them. Signing out always works.
export default function BillingLocked() {
  const { user, logout, checkAuth, isImpersonating, stopImpersonating } = useAuth();
  const navigate = useNavigate();
  const [signingOut, setSigningOut] = useState(false);
  const isAdmin = user?.role_name === 'Admin';

  const signOut = async () => {
    setSigningOut(true);
    try {
      await logout();
    } catch (e) {
      // Still signed in; the button is offered again below.
    }
    setSigningOut(false);
    navigate('/login');
  };

  // Once billing goes from locked to open (say, Stripe sent the Admin back
  // and the webhook has arrived), reload who is signed in so the app comes
  // back without a page reload. Only on that change, so a server that still
  // answers 402 cannot make this loop.
  const sawLocked = useRef(false);
  const onLoaded = (data) => {
    if (!data) return;
    if (data.locked) sawLocked.current = true;
    else if (sawLocked.current && checkAuth) {
      sawLocked.current = false;
      checkAuth();
    }
  };

  return (
    <AuthCard subtitle="Workspace locked">
      {isAdmin ? (
        <>
          <p style={{ marginTop: 0, color: 'var(--text-primary)' }}>
            Your company's ActiveOptics workspace is locked until it has an active subscription.
            Nothing has been deleted: everyone gets back in as soon as billing is sorted.
          </p>
          <BillingPanel compact onLoaded={onLoaded} />
        </>
      ) : (
        <p role="status" style={{ marginTop: 0, color: 'var(--text-primary)' }}>
          Your company's ActiveOptics subscription needs attention. Ask your administrator.
        </p>
      )}
      <div style={{ display: 'flex', gap: '8px', marginTop: '24px', flexWrap: 'wrap' }}>
        {isImpersonating && (
          <button type="button" className="btn btn-secondary" onClick={() => stopImpersonating()}>
            Return to your account
          </button>
        )}
        <button type="button" className="btn btn-secondary" onClick={signOut} disabled={signingOut}>
          <LogOut size={16} /> {signingOut ? 'Signing out...' : 'Sign out'}
        </button>
      </div>
    </AuthCard>
  );
}
